import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmt, rm } from "@/lib/format";
import { useDashboardCtx } from "@/context/DashboardContext";
import { ArrowUpRight, ArrowDownRight, Loader2 } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";

const colorMap = {
  blue: "text-chart-3", emerald: "text-accent", amber: "text-primary",
};

export default function Overview() {
  const { customerId } = useDashboardCtx();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/overview", { params: customerId ? { customer_id: customerId } : {} })
      .then((r) => setData(r.data)).catch(() => {});
  }, [customerId]);

  if (!data) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const maxFunnel = Math.max(...data.funnel.map((f) => f.value));

  return (
    <div className="space-y-6 animate-float-up" data-testid="overview-panel">
      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {data.kpis.map((k) => {
          const good = k.trend === "up" || k.trend === "down_good";
          return (
            <div key={k.id} data-testid={`kpi-${k.id}`} className="bg-card border border-border rounded-2xl p-5 hover:border-primary/40 transition-colors duration-200">
              <p className="text-xs text-muted-foreground mb-2">{k.title}</p>
              <p className={`font-display text-2xl font-extrabold ${colorMap[k.color] || ""}`}>{fmt(k.value, k.format)}</p>
              <p className={`text-xs font-mono mt-2 flex items-center gap-1 ${good ? "text-accent" : "text-destructive"}`}>
                {good ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}{k.change}
              </p>
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Trend chart */}
        <div className="lg:col-span-2 bg-card border border-border rounded-2xl p-5">
          <h3 className="font-display font-semibold mb-4">Spend vs Revenue (7 days)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.trend} margin={{ left: -10, right: 10 }}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F59E0B" stopOpacity={0.5} /><stop offset="100%" stopColor="#F59E0B" stopOpacity={0} /></linearGradient>
                <linearGradient id="spd" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.4} /><stop offset="100%" stopColor="#10B981" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="day" stroke="#9CA3AF" fontSize={12} />
              <YAxis stroke="#9CA3AF" fontSize={12} />
              <Tooltip contentStyle={{ background: "#111827", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} formatter={(v) => rm(v)} />
              <Area type="monotone" dataKey="revenue" stroke="#F59E0B" fill="url(#rev)" strokeWidth={2} />
              <Area type="monotone" dataKey="spend" stroke="#10B981" fill="url(#spd)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Channel */}
        <div className="bg-card border border-border rounded-2xl p-5">
          <h3 className="font-display font-semibold mb-4">Channel Split</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.channel} margin={{ left: -10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="name" stroke="#9CA3AF" fontSize={12} />
              <YAxis stroke="#9CA3AF" fontSize={12} />
              <Tooltip contentStyle={{ background: "#111827", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} formatter={(v) => rm(v)} />
              <Bar dataKey="spend" fill="#10B981" radius={[6, 6, 0, 0]} />
              <Bar dataKey="revenue" fill="#F59E0B" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Funnel */}
      <div className="bg-card border border-border rounded-2xl p-5">
        <h3 className="font-display font-semibold mb-5">Revenue Attribution Funnel</h3>
        <div className="space-y-3">
          {data.funnel.map((f, i) => (
            <div key={f.label} data-testid={`funnel-row-${i}`} className="flex items-center gap-4">
              <span className="w-40 shrink-0 text-sm text-muted-foreground">{f.label}</span>
              <div className="flex-1 bg-secondary/60 rounded-lg h-9 overflow-hidden">
                <div className={`h-full rounded-lg flex items-center justify-end px-3 ${i >= 4 ? "bg-accent/70" : "bg-primary/70"}`} style={{ width: `${Math.max((f.value / maxFunnel) * 100, 8)}%` }}>
                  <span className="text-xs font-mono font-semibold text-background">{fmt(f.value, f.format)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
